import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { LinesSheet } from './LinesSheet';
import { BLOCKS_RESOURCE, TEMPLATES_KEY, toDraft, toWire, validQuantities, type Block, type DraftLine } from './types';
import './templates.css';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

function BlockForm({ initial, onClose }: { initial?: Block; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { me } = useMe();
  const [base, setBase] = useState(initial);
  const canEdit = !initial || (me !== undefined && can(me, BLOCKS_RESOURCE, 'edit'));
  // a role may see blocks but not their content: then lines are neither shown nor sent
  const linesVisible = !initial || initial.lines !== undefined;
  const [name, setName] = useState(initial?.name ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [lines, setLines] = useState<DraftLine[]>((initial?.lines ?? []).map(toDraft));
  const [localError, setLocalError] = useState('');
  const [saved, setSaved] = useState(false);
  // the notes under ⚙ (the sheet first, like the quote — owner remark 4/32); open at once for a new block
  const [showSettings, setShowSettings] = useState(!initial);

  const load = (block: Block) => {
    setBase(block);
    setName(block.name);
    setNotes(block.notes);
    setLines((block.lines ?? []).map(toDraft));
  };

  const save = useMutation({
    mutationFn: () => {
      const wire = toWire(lines);
      if (!base) return api<Block>('/api/templates/blocks', { method: 'POST', body: { name, notes, lines: wire } });
      // only what changed (rule 7a: a field the role may not see is never sent)
      const original = JSON.stringify(toWire((base.lines ?? []).map(toDraft)));
      const changed = {
        ...(name !== base.name ? { name } : {}),
        ...(notes !== base.notes ? { notes } : {}),
        ...(linesVisible && JSON.stringify(wire) !== original ? { lines: wire } : {}),
      };
      return api<Block>(`/api/templates/blocks/${base.id}`, { method: 'PATCH', body: { version: base.version, ...changed } });
    },
    onSuccess: async (block) => {
      load(block);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const conflict = error?.status === 409;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    if (!validQuantities(lines)) {
      setLocalError('Ilość w każdym wierszu musi być liczbą całkowitą większą od zera.');
      return;
    }
    setLocalError('');
    save.mutate();
  }

  // owner 2026-10-23 21:14: "Zamknij", another one or another screen with unsaved changes asks first
  const plain = (ls: DraftLine[]) => ls.map(({ key: _key, ...l }) => ({ ...l, quantity: l.quantity.trim() }));
  const guardRef = useUnsavedGuard({
    dirty: canEdit && !sameValues({ name, notes, lines: plain(lines) }, { name: base?.name ?? '', notes: base?.notes ?? '', lines: plain((base?.lines ?? []).map(toDraft)) }),
    save: async (el) => {
      if (!validQuantities(lines)) {
        setLocalError('Ilość w każdym wierszu musi być liczbą całkowitą większą od zera.');
        return false;
      }
      if (!validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });
  return (
    <form ref={guardRef} className="panel form wide tpl-editor" aria-label={base ? `Blok: ${base.name}` : 'Nowy blok'} data-feature-id="templates.blocks" onSubmit={submit}>
      <h2>{base ? `Blok: ${base.name}` : 'Nowy blok'}</h2>
      {conflict && (
        <ConflictNotice
          onReload={() => {
            const current = error.current as Block | undefined;
            void (current ? Promise.resolve(current) : api<Block>(`/api/templates/blocks/${base!.id}`)).then(load);
            save.reset();
          }}
        />
      )}
      {error && !conflict && (
        <p className="alert" role="alert">
          {error.message}
          {fieldIssues(error).map((i) => (
            <span key={i.path + i.message}> {i.message}</span>
          ))}
        </p>
      )}
      {localError && (
        <p className="alert" role="alert">
          {localError}
        </p>
      )}
      {saved && !error && (
        <Notice onClose={() => setSaved(false)}>
          Zapisano.
        </Notice>
      )}
      <label className="field">
        <span>Nazwa</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" readOnly={!canEdit} placeholder="np. Mikrofony" />
      </label>
      <div className="tpl-settings-bar">
        <span className="muted">{notes.trim() ? notes.trim().split('\n')[0] : 'Bez opisu'}</span>
        <button className="btn icon ghost tpl-gear" type="button" aria-label="Ustawienia bloku" aria-expanded={showSettings} title="Ustawienia bloku: opis" onClick={() => setShowSettings(!showSettings)}>
          ⚙
        </button>
      </div>
      {showSettings && (
        <div className="tpl-settings" role="group" aria-label="Ustawienia bloku">
          <label className="field">
            <span>Uwagi</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} readOnly={!canEdit} />
          </label>
        </div>
      )}
      {linesVisible && <LinesSheet lines={lines} onChange={setLines} canEdit={canEdit} label="Dodaj do bloku" featureId="templates.blocks" />}
      <div className="actions">
        {canEdit && (
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj blok'}
          </button>
        )}
        <button className="btn" type="button" onClick={(e) => leaveGuarded(onClose, e.currentTarget.closest('form'))}>
          Zamknij
        </button>
      </div>
    </form>
  );
}

/** Add (no `blockId`) or edit a block. */
export function BlockEditor({ blockId, onClose }: { blockId?: string; onClose: () => void }) {
  const detail = useQuery({ queryKey: [...TEMPLATES_KEY, 'block', blockId], enabled: blockId !== undefined, gcTime: 0, queryFn: () => api<Block>(`/api/templates/blocks/${blockId}`) });
  if (blockId === undefined) return <BlockForm onClose={onClose} />;
  if (detail.error) {
    return (
      <p className="alert" role="alert">
        {detail.error instanceof ApiError ? detail.error.message : 'Nie udało się wczytać bloku'}
      </p>
    );
  }
  if (!detail.data) return <p className="muted" role="status">Wczytywanie…</p>;
  return <BlockForm key={detail.data.id} initial={detail.data} onClose={onClose} />;
}
